import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { UserProfile } from '../types';
import { useNavigate } from 'react-router-dom';
import PageTitle from './PageTitle';
// Import ShadCN UI components
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Label } from './ui/label';
import { Select, SelectItem } from './ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Upload, X, File, FileText } from 'lucide-react';

export default function AccountSettings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<Partial<UserProfile>>({});
  const [originalProfile, setOriginalProfile] = useState<Partial<UserProfile>>({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    if (user) {
      fetchProfile();
    }
  }, [user]);

  // Initialize the address field from street_address and city_state_zip
  useEffect(() => {
    if (profile.street_address || profile.city_state_zip) {
      const addressParts: string[] = [];
      if (profile.street_address) addressParts.push(profile.street_address);
      if (profile.city_state_zip) addressParts.push(profile.city_state_zip);
      
      if (addressParts.length > 0) {
        setProfile(prev => ({
          ...prev,
          address: addressParts.join(', ')
        }));
      }
    }
  }, [profile.street_address, profile.city_state_zip]);

  const fetchProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user?.id)
        .single();

      if (error) throw error;
      if (data) setProfile(data);
      if (data) setOriginalProfile(data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const validateEmail = (email: string) => {
    if (!email) return true; // Allow empty email during typing
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(email);
  };

  const formatPhone = (input: string): string => {
    // Remove all non-digits
    const cleaned = input.replace(/\D/g, '');
    
    // Limit to 10 digits
    const digits = cleaned.slice(0, 10);
    
    // Format based on number of digits
    if (digits.length >= 3 && digits.length <= 6) {
      return `(${digits.slice(0, 3)})${digits.length > 3 ? ' ' + digits.slice(3) : ''}`;
    } else if (digits.length > 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length > 0) {
      return `(${digits}`;
    }
    return '';
  };

  const validatePhone = (phone: string) => {
    if (!phone) return true; // Allow empty phone during typing
    const digits = phone.replace(/\D/g, '');
    return digits.length === 10;
  };

  // Define handleSaveProfile for profile updates
  const handleSaveProfile = useCallback(async (profileData: Partial<UserProfile>) => {
    if (!user) return;
    try {
      setLoading(true);
      
      // Create a properly formatted profile object for saving
      const profileToSave = {
        id: user.id,
        updated_at: new Date().toISOString(),
        ...profileData
      };

      const { error } = await supabase
        .from('profiles')
        .upsert(profileToSave);

      if (error) throw error;
      
      // Show success indicator
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      
      // Update original profile to match current state
      setOriginalProfile(profileData);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage({ type: 'error', text: 'Failed to update profile' });
    } finally {
      setLoading(false);
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset errors
    setErrors({});
    const newErrors: { [key: string]: string } = {};

    // Validate email
    if (profile.email && !validateEmail(profile.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Validate phone
    if (profile.phone && !validatePhone(profile.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    // If there are errors, don't submit
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setMessage({ type: 'error', text: 'Please fix the errors before submitting' });
      return;
    }

    // Use the handleSaveProfile function for consistency
    await handleSaveProfile(profile);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    let newValue = value;
    let errorMessage = '';

    if (name === 'phone') {
      newValue = formatPhone(value);
      if (newValue && !validatePhone(newValue)) {
        errorMessage = 'Invalid phone number';
      }
    } else if (name === 'email') {
      if (value && !validateEmail(value)) {
        errorMessage = 'Invalid email';
      }
    }

    setProfile(prev => ({ ...prev, [name]: newValue }));
    setErrors(prev => ({ ...prev, [name]: errorMessage }));
  };

  // Resume upload handlers
  const handleDragEnter = useCallback((e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (file.type === 'application/pdf' || 
          file.type === 'application/msword' || 
          file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        handleFileUpload(file);
      } else {
        setMessage({ 
          type: 'error', 
          text: 'Please upload a PDF or Word document (.pdf, .doc, .docx)' 
        });
      }
    }
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (file.type === 'application/pdf' || 
          file.type === 'application/msword' || 
          file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        handleFileUpload(file);
      } else {
        setMessage({ 
          type: 'error', 
          text: 'Please upload a PDF or Word document (.pdf, .doc, .docx)' 
        });
      }
    }
  }, []);

  const handleFileUpload = async (file: File) => {
    if (!user) return;
    
    try {
      setUploading(true);
      setUploadProgress(0);
      
      // Create a unique file name
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}-resume-${Date.now()}.${fileExt}`;
      
      // Upload to Supabase Storage - do NOT include 'resumes/' in the path since
      // we're already specifying the bucket name
      const { data, error } = await supabase.storage
        .from('resumes')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true,
        });
      
      if (error) throw error;
      
      // Get a signed URL (works with private buckets)
      const { data: signedUrlData } = await supabase.storage
        .from('resumes')
        .createSignedUrl(fileName, 60 * 60 * 24 * 7); // URL valid for 7 days
      
      // Update profile with new resume info
      const updatedProfile = {
        ...profile,
        resume_url: signedUrlData?.signedUrl,
        resume_name: file.name
      };
      
      setProfile(updatedProfile);
      
      // Save profile changes
      await handleSaveProfile(updatedProfile);
      
      // Parse resume text and store it in the resume_text field
      try {
        // Get the current session for authentication
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('No session found');
        
        // Call the new backend endpoint
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const response = await fetch(`${apiUrl}/profile/parse-resume`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ fileName: fileName }),
        });

        if (!response.ok) {
          const errMsg = await response.text();
          throw new Error(`Server parse failed: ${errMsg}`);
        }
        
        const result = await response.json();
        setMessage({ type: 'success', text: 'Resume uploaded & parsed successfully!' });
      } catch (parseError) {
        // Still show success for upload but mention parsing issue
        setMessage({ 
          type: 'success', 
          text: 'Resume uploaded successfully, but there was an issue extracting text.'
        });
      }
      
      setUploadProgress(100);
    } catch (error) {
      console.error('Error uploading resume:', error);
      setMessage({ type: 'error', text: 'Failed to upload resume' });
    } finally {
      setUploading(false);
    }
  };
  
  // This is intentionally removed to avoid duplicate declarations of handleSaveProfile

  const removeResume = useCallback(async () => {
    if (!user || !profile.resume_url) return;
    
    try {
      // Extract the file name from the URL - the filename is the last part of the URL
      const urlParts = profile.resume_url.split('/');
      const fileName = urlParts[urlParts.length - 1].split('?')[0]; // Remove query params if present
      
      // Delete from Supabase Storage
      const { error } = await supabase.storage
        .from('resumes')
        .remove([fileName]);
      
      if (error) throw error;
      
      // Update profile with resume removed and resume_text cleared
      const updatedProfile = {
        ...profile,
        resume_url: undefined,
        resume_name: undefined,
        resume_text: null // Clear the resume text as well
      };
      
      setProfile(updatedProfile);
      setMessage({ type: 'success', text: 'Resume removed successfully!' });
      
      // Auto-save the profile without the resume
      await handleSaveProfile(updatedProfile);
    } catch (error) {
      console.error('Error removing resume:', error);
      setMessage({ type: 'error', text: 'Failed to remove resume' });
    }
  }, [user, profile, handleSaveProfile]);

  // Update hasUnsavedChanges whenever profile changes
  useEffect(() => {
    // Skip the initial check when profiles are not yet loaded
    if (Object.keys(originalProfile).length === 0) return;
    
    // Deep comparison of objects
    const hasChanges = JSON.stringify(originalProfile) !== JSON.stringify(profile);
    setHasUnsavedChanges(hasChanges);
  }, [profile, originalProfile]);

  // Add a warning when user tries to leave with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        // Standard for modern browsers
        event.preventDefault();
        // Required for older browsers
        event.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return event.returnValue;
      }
    };

    // Add event listener for page unload/refresh
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Clean up when component unmounts
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Auto-dismiss error messages after 6 seconds
  useEffect(() => {
    if (message && message.type === 'error') {
      const timer = setTimeout(() => {
        setMessage(null);
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  return (
    <div className="container py-8 max-w-4xl mx-auto">
      <PageTitle title="Cover Letter Context" />
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between">
          <h1 className="text-3xl font-bold tracking-tight">Profile</h1>
          <Button 
            type="submit" 
            form="profile-form"
            disabled={loading} 
            className={`flex items-center gap-2 transition-colors duration-200 ${saveSuccess ? 'bg-green-500 hover:bg-green-600' : ''}`}
          >
            {loading ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save Changes'}
          </Button>
        </div>
        
        <Card className="w-full">
          <CardContent>
          {message && message.type === 'error' && (
            <Alert variant="destructive" className="mb-6">
              <AlertDescription>{message.text}</AlertDescription>
            </Alert>
          )}

          <form id="profile-form" onSubmit={handleSubmit} className="space-y-6 pt-4 pb-2">
            <div className="space-y-2 mb-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-medium">Resume</h3>
              </div>
              
              {profile.resume_url ? (
                <div className="flex flex-wrap sm:flex-nowrap items-center gap-3 p-3 border rounded-md bg-muted/30 mb-6">
                  <File className="h-5 w-5 text-primary flex-shrink-0" />
                  <div className="flex-1 truncate min-w-0">
                    <span className="text-foreground truncate block">
                      {profile.resume_name}
                    </span>
                  </div>
                  <div className="flex space-x-2 flex-shrink-0 ml-auto">
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm" 
                      className="text-primary hover:text-primary/80 border-primary/20 hover:border-primary/30"
                      onClick={async () => {
                        try {
                          // Extract the file name from the URL or use the stored name
                          const fileName = profile.resume_name ? 
                            profile.resume_name.split('/').pop() : 
                            (profile.resume_url ? profile.resume_url.split('/').pop()?.split('?')[0] : '');
                            
                          if (!fileName) {
                            throw new Error('Could not determine filename');
                          }
                          
                          // Create a fresh signed URL
                          const { data } = await supabase.storage
                            .from('resumes')
                            .createSignedUrl(fileName, 60 * 60); // Valid for 1 hour
                            
                          if (data?.signedUrl) {
                            window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
                          } else if (profile.resume_url) {
                            // Fallback to stored URL if we can't create a signed one
                            window.open(profile.resume_url, '_blank', 'noopener,noreferrer');
                          }
                        } catch (error) {
                          console.error('Error creating signed URL:', error);
                          // Fallback to the stored URL if error occurs
                          if (profile.resume_url) {
                            window.open(profile.resume_url, '_blank', 'noopener,noreferrer');
                          }
                        }
                      }}
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm" 
                      onClick={removeResume} 
                      className="text-destructive hover:text-destructive/90 border-destructive/20 hover:border-destructive/30"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="border border-dashed border-border rounded-lg p-4 mb-6">
                  <label
                    htmlFor="resume-upload"
                    className={`
                      flex flex-col items-center justify-center 
                      rounded-md p-4 h-32
                      cursor-pointer transition-colors duration-200
                      ${isDragging ? 'bg-primary/5 border-primary' : 'hover:bg-muted/50'}
                    `}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  >
                    <input
                      type="file"
                      id="resume-upload"
                      name="resume"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    {uploading ? (
                      <div className="flex flex-col items-center">
                        <Upload className="h-8 w-8 text-primary mb-2 animate-pulse" />
                        <p className="text-sm text-foreground mb-2">Uploading your resume...</p>
                        <div className="w-48 h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary rounded-full transition-all duration-300" 
                            style={{ width: `${uploadProgress}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{uploadProgress}%</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <Upload className="h-8 w-8 text-primary mb-2" />
                        <p className="text-sm font-medium mb-1">
                          <span className="text-primary">Click to upload</span> or drag and drop
                        </p>
                        <span className="text-xs text-muted-foreground">(PDF, DOC, DOCX up to 10MB)</span>
                      </div>
                    )}
                  </label>
                </div>
              )}
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="full_name">
                Full Name
                {originalProfile.full_name !== profile.full_name && profile.full_name !== undefined && 
                  <span className="text-destructive font-normal ml-1">(unsaved changes)</span>
                }
              </Label>
                <Input
                  id="full_name"
                  name="full_name"
                  value={profile.full_name || ''}
                  onChange={handleChange}
                  placeholder="Your full name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">
                Phone
                {originalProfile.phone !== profile.phone && profile.phone !== undefined && 
                  <span className="text-destructive font-normal ml-1">(unsaved changes)</span>
                }
              </Label>
                <Input
                  id="phone"
                  name="phone"
                  value={profile.phone || ''}
                  onChange={handleChange}
                  placeholder="(123) 456-7890"
                  maxLength={14} // (XXX) XXX-XXXX = 14 chars
                  className={errors.phone ? "border-destructive" : ""}
                />
                {errors.phone && (
                  <p className="text-sm font-medium text-destructive">{errors.phone}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">
                Email
                {originalProfile.email !== profile.email && profile.email !== undefined && 
                  <span className="text-destructive font-normal ml-1">(unsaved changes)</span>
                }
              </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={profile.email || ''}
                  onChange={handleChange}
                  placeholder="email@example.com"
                  className={errors.email ? "border-destructive" : ""}
                />
                {errors.email && (
                  <p className="text-sm font-medium text-destructive">{errors.email}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">
                Address
                {(originalProfile.street_address !== profile.street_address || originalProfile.city_state_zip !== profile.city_state_zip) && 
                  <span className="text-destructive font-normal ml-1">(unsaved changes)</span>
                }
              </Label>
              <Input
                id="address"
                name="address"
                value={profile.address || ''}
                onChange={handleChange}
                placeholder="123 Main St, New York, NY 10001"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="education">
                Education
                {originalProfile.education !== profile.education && profile.education !== undefined && 
                  <span className="text-destructive font-normal ml-1">(unsaved changes)</span>
                }
              </Label>
              <Textarea
                id="education"
                name="education"
                value={profile.education || ''}
                onChange={handleChange}
                placeholder="Add any educational background not included in your resume"
                className="min-h-[120px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="experience">
                Professional Experience
                {originalProfile.experience !== profile.experience && profile.experience !== undefined && 
                  <span className="text-destructive font-normal ml-1">(unsaved changes)</span>
                }
              </Label>
              <Textarea
                id="experience"
                name="experience"
                value={profile.experience || ''}
                onChange={handleChange}
                placeholder="Add any work experience not included in your resume"
                className="min-h-[120px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="skills">
                Skills
                {originalProfile.skills !== profile.skills && profile.skills !== undefined && 
                  <span className="text-destructive font-normal ml-1">(unsaved changes)</span>
                }
              </Label>
              <Textarea
                id="skills"
                name="skills"
                value={profile.skills || ''}
                onChange={handleChange}
                placeholder="Add any relevant skills not included in your resume"
                className="min-h-[120px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="additional_notes">
                Custom Instructions for the AI
                {originalProfile.additional_notes !== profile.additional_notes && profile.additional_notes !== undefined && 
                  <span className="text-destructive font-normal ml-1">(unsaved changes)</span>
                }
              </Label>
              <Textarea
                id="additional_notes"
                name="additional_notes"
                value={profile.additional_notes || ''}
                onChange={handleChange}
                placeholder="Custom instructions to guide the AI when generating your cover letters"
                className="min-h-[120px]"
              />
            </div>
            
            <div className="mt-6">
              {/* Removed the button from here since we moved it to the header */}
            </div>
          </form>
          </CardContent>
          <CardFooter className="flex justify-between">
            <span></span> {/* Empty span for flex spacing */}
            {/* Footer content can go here if needed */}
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
